import React from 'react';
import { MapPin, Navigation } from 'lucide-react';

export const LocationMap: React.FC = () => {
  return (
    <section className="py-16 bg-gray-50" id="location">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
            
            {/* Directions Info */}
            <div>
                <span className="text-riw-primary font-bold tracking-wider uppercase text-sm">Lokasi & Rute</span>
                <h2 className="text-3xl font-bold text-gray-900 mt-2 mb-6">Mudah Ditemukan di Dalam KBS</h2>
                
                <div className="space-y-6">
                    <div className="flex gap-4">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                            <span className="font-bold text-green-700">1</span>
                        </div>
                        <div>
                            <h4 className="font-bold text-gray-800">Masuk Gerbang Utama</h4>
                            <p className="text-gray-600 text-sm">Masuk melalui pintu utama Kebun Binatang Surabaya di Jl. Setro (depan Patung Suroboyo).</p>
                        </div>
                    </div>
                    
                    <div className="flex gap-4">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                            <span className="font-bold text-green-700">2</span>
                        </div>
                        <div>
                            <h4 className="font-bold text-gray-800">Ikuti Jalur Utama</h4>
                            <p className="text-gray-600 text-sm">Berjalan lurus melewati area Aquarium, kemudian ambil arah kanan menuju area Tunggang Satwa.</p>
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <div className="w-10 h-10 rounded-full bg-giz-light flex items-center justify-center flex-shrink-0">
                            <MapPin className="w-5 h-5 text-giz-primary" />
                        </div>
                        <div>
                            <h4 className="font-bold text-gray-800">Tiba di GIZ & RIW</h4>
                            <p className="text-gray-600 text-sm">GIZ Park dan RIW terletak bersebelahan di dekat area Pulau Bekantan. Lihat signage besar berwarna Ungu & Oranye.</p>
                        </div>
                    </div>
                </div>

                <div className="mt-8 p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
                    <h5 className="font-bold flex items-center gap-2 mb-2">
                        <Navigation className="w-4 h-4 text-blue-500" /> Tips Berkunjung
                    </h5>
                    <p className="text-sm text-gray-600">
                        Kami menyarankan untuk membeli tiket Fast Track secara online agar bisa langsung scan barcode tanpa antre di loket wahana.
                    </p>
                </div>
            </div>

            {/* Stylized Map Visualization */}
            <div className="relative bg-white p-4 rounded-3xl shadow-xl border border-gray-100">
                <div className="aspect-[4/3] bg-emerald-50 rounded-2xl relative overflow-hidden border border-emerald-100">
                    {/* Abstract Map Drawing */}
                    <svg viewBox="0 0 400 300" className="w-full h-full">
                        {/* Background / KBS Area */}
                        <path d="M0,0 H400 V300 H0 Z" fill="#ecfdf5" />
                        
                        {/* Roads */}
                        <path d="M200,300 V250 C200,200 100,200 100,150 V100" fill="none" stroke="#d1fae5" strokeWidth="20" strokeLinecap="round"/>
                        <path d="M200,250 C200,200 300,200 300,150 V120" fill="none" stroke="#d1fae5" strokeWidth="20" strokeLinecap="round"/>
                        
                        {/* Main Gate */}
                        <circle cx="200" cy="280" r="15" fill="#15803d" />
                        <text x="200" y="295" fontSize="10" textAnchor="middle" fill="white" fontWeight="bold">Masuk</text>

                        {/* Lake / Feature */}
                        <path d="M50,50 Q100,20 150,50 T250,50" fill="none" stroke="#60a5fa" strokeWidth="15" opacity="0.5"/>

                        {/* GIZ Location */}
                        <g className="cursor-pointer hover:opacity-80 transition-opacity">
                            <circle cx="100" cy="90" r="25" fill="#4F46E5" className="animate-pulse" />
                            <text x="100" y="95" fontSize="12" textAnchor="middle" fill="white" fontWeight="bold">GIZ</text>
                            <rect x="75" y="120" width="50" height="20" rx="4" fill="white" opacity="0.9" />
                            <text x="100" y="133" fontSize="8" textAnchor="middle" fill="#333">Digital Zoo</text>
                        </g>

                        {/* RIW Location */}
                        <g className="cursor-pointer hover:opacity-80 transition-opacity">
                            <circle cx="300" cy="100" r="25" fill="#FB923C" className="animate-pulse" style={{animationDelay: '0.5s'}} />
                            <text x="300" y="105" fontSize="12" textAnchor="middle" fill="white" fontWeight="bold">RIW</text>
                            <rect x="275" y="130" width="50" height="20" rx="4" fill="white" opacity="0.9" />
                            <text x="300" y="143" fontSize="8" textAnchor="middle" fill="#333">Rabbit Land</text>
                        </g>

                        {/* Dotted Line Route */}
                        <path d="M200,280 Q200,220 100,120" fill="none" stroke="#4F46E5" strokeWidth="2" strokeDasharray="4,4" />
                        <path d="M200,280 Q200,220 300,130" fill="none" stroke="#FB923C" strokeWidth="2" strokeDasharray="4,4" />
                    </svg>

                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-2 rounded-lg text-xs shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 mb-1">
                            <div className="w-3 h-3 rounded-full bg-giz-primary"></div> GIZ Park
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-riw-primary"></div> RIW
                        </div>
                    </div>
                </div>
                <p className="text-center text-xs text-gray-400 mt-3">*Ilustrasi peta lokasi di dalam area Kebun Binatang Surabaya</p>
            </div>
        </div>
      </div>
    </section>
  );
};